import type {
  ComputeCaseDto,
  ListComputeSettingsModelDto,
  ModelAreaDto,
} from '../../../api/generated';

export const normalizeObjectModelAreaId = (
  areaId: string | null | undefined,
) => {
  const normalized = areaId?.trim().toLowerCase() ?? '';
  return normalized === '00000000-0000-0000-0000-000000000000'
    ? ''
    : normalized;
};

const normalizeName = (name: string | null | undefined) =>
  name?.trim().toLowerCase() ?? '';

export const getObjectArchelSettings = (
  settings: ListComputeSettingsModelDto[] | null | undefined,
) =>
  settings?.filter(
    (setting) =>
      setting.computeMethod === 'ObjectEstimation' &&
      (normalizeName(setting.name).includes('channel') ||
        normalizeName(setting.name).includes('mouthbar')),
  ) ?? [];

export const isObjectModelAreaTaken = (
  cases: ComputeCaseDto[],
  areaId: string | null | undefined,
  method: ComputeCaseDto['computeMethod'],
  currentCaseId = '',
) =>
  cases.some(
    (existingCase) =>
      existingCase.computeMethod === method &&
      existingCase.computeCaseId !== currentCaseId &&
      normalizeObjectModelAreaId(existingCase.modelArea?.modelAreaId) ===
        normalizeObjectModelAreaId(areaId),
  );

export const getObjectEstimationAvailability = (
  cases: ComputeCaseDto[],
  modelAreas: ModelAreaDto[],
  archelSettings: ListComputeSettingsModelDto[] | null | undefined,
  currentCaseId = '',
) => {
  const archels = archelSettings ?? [];
  const knownNames = new Set(
    archels.map((setting) => normalizeName(setting.name)),
  );
  const pairKey = (areaId: string | null | undefined, name: string) =>
    JSON.stringify([normalizeObjectModelAreaId(areaId), normalizeName(name)]);
  const takenPairs = new Set<string>();

  cases
    .filter(
      (existingCase) =>
        existingCase.computeMethod === 'ObjectEstimation' &&
        existingCase.computeCaseId !== currentCaseId,
    )
    .forEach((existingCase) => {
      existingCase.inputSettings.forEach((setting) => {
        const name = normalizeName(setting.name);
        if (name && knownNames.has(name)) {
          takenPairs.add(pairKey(existingCase.modelArea?.modelAreaId, name));
        }
      });
    });

  const isDuplicateCombination = (areaId: string, archelName: string) =>
    takenPairs.has(pairKey(areaId, archelName));
  const isModelAreaFullyUsed = (area: ModelAreaDto) =>
    archels.length > 0 &&
    archels.every((archel) =>
      isDuplicateCombination(area.modelAreaId ?? '', archel.name ?? ''),
    );
  const isModelAreaDisabled = (
    area: ModelAreaDto,
    selectedArchels: ListComputeSettingsModelDto[] | undefined,
  ) =>
    selectedArchels?.length
      ? selectedArchels.some((archel) =>
          isDuplicateCombination(area.modelAreaId ?? '', archel.name ?? ''),
        )
      : isModelAreaFullyUsed(area);
  const isArchelTaken = (
    archel: ListComputeSettingsModelDto,
    selectedAreas: ModelAreaDto[] | undefined,
  ) => {
    const areas = selectedAreas?.length ? selectedAreas : modelAreas;
    return (
      areas.length > 0 &&
      areas.every((area) =>
        isDuplicateCombination(area.modelAreaId ?? '', archel.name ?? ''),
      )
    );
  };

  return {
    takenPairs,
    isDuplicateCombination,
    isModelAreaFullyUsed,
    isModelAreaDisabled,
    isArchelTaken,
    getAvailableArchels: (selectedAreas: ModelAreaDto[] | undefined) =>
      archels.filter((archel) => !isArchelTaken(archel, selectedAreas)),
    isExhausted:
      modelAreas.length > 0 && modelAreas.every(isModelAreaFullyUsed),
  };
};
